import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { TabBar } from "@/components/toggles";
import { ArrowLeft, VolumeX, Volume2, UserMinus, Users } from "lucide-react";

/**
 * "Ich folge" — Rebrand von MutedSellersPage (iter106 · 2026-07-19).
 *
 * 3-Tab-Layout: Social · Marketplace · stilltouchedX
 * Zeigt vom User gefolgte Profile mit:
 *  - Mute-Toggle (POST/DELETE /api/me/seller-mutes)
 *  - Unfollow-Button (DELETE /api/profiles/{id}/follow)
 *
 * Datenquelle: GET /api/me/following?area=SOCIAL|MP|STX
 */
const TABS = [
  { key: "SOCIAL", labelKey: "i_follow.tab_social", emptyKey: "i_follow.empty_social" },
  { key: "MP", labelKey: "i_follow.tab_mp", emptyKey: "i_follow.empty_mp" },
  { key: "STX", labelKey: "i_follow.tab_stx", emptyKey: "i_follow.empty_stx" },
];

export default function MutedSellersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState("SOCIAL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (area) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/following?area=${area}`);
      setRows(data.follows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const toggleMute = async (row) => {
    try {
      if (row.is_muted) {
        await api.delete(`/me/seller-mutes/${row.profile_id}`);
      } else {
        await api.post(`/me/seller-mutes`, { seller_id: row.profile_id });
      }
      setRows((prev) => prev.map((r) => r.profile_id === row.profile_id ? { ...r, is_muted: !r.is_muted } : r));
    } catch { /* noop */ }
  };

  const unfollow = async (row) => {
    if (!window.confirm(t("i_follow.confirm_unfollow"))) return;
    try {
      await api.delete(`/profiles/${row.profile_id}/follow`);
      setRows((prev) => prev.filter((r) => r.profile_id !== row.profile_id));
    } catch { /* noop */ }
  };

  const currentTab = TABS.find((tt) => tt.key === tab) || TABS[0];

  return (
    <div data-testid="i-follow-page" className="bg-white min-h-full">
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button data-testid="i-follow-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2">
            <Users size={16} /> {t("i_follow.title")}
          </h1>
          <p className="text-xs text-[#999]">{t("i_follow.subtitle")}</p>
        </div>
      </div>

      {/* Tabs */}
      <TabBar
        testId="i-follow-tabs"
        tabs={TABS.map((tt) => ({
          key: tt.key,
          label: t(tt.labelKey),
          testId: `i-follow-tab-${tt.key}`,
        }))}
        activeKey={tab}
        onChange={setTab}
        className="mt-2"
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="i-follow-empty" className="px-4 py-16 text-center text-sm text-[#999]">
          {t(currentTab.emptyKey)}
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {rows.map((r) => (
            <div
              key={r.profile_id}
              data-testid={`i-follow-row-${r.profile_id}`}
              className="flex items-center gap-3 px-4 py-3"
            >
              <button
                onClick={() => r.handle && navigate(`/user/${r.handle}`)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <div className="w-9 h-9 rounded-full bg-[#F5F5F5] overflow-hidden shrink-0">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-[#999]">
                      {(r.display_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{r.display_name || r.handle || "—"}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {r.handle && <p className="text-[10px] text-[#999] truncate">@{r.handle}</p>}
                    {r.is_muted && (
                      <span data-testid={`i-follow-muted-badge-${r.profile_id}`}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-amber-50 text-amber-700 border border-amber-200">
                        <VolumeX size={9} /> {t("i_follow.muted_badge")}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              <button
                data-testid={`i-follow-mute-${r.profile_id}`}
                onClick={() => toggleMute(r)}
                title={r.is_muted ? t("i_follow.unmute") : t("i_follow.mute")}
                className={`p-2 border transition-colors ${
                  r.is_muted
                    ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                    : "border-[#E5E5E5] text-[#666] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                }`}
              >
                {r.is_muted ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>

              <button
                data-testid={`i-follow-unfollow-${r.profile_id}`}
                onClick={() => unfollow(r)}
                title={t("i_follow.unfollow")}
                className="p-2 border border-[#E5E5E5] text-[#666] hover:border-red-500 hover:text-red-500 transition-colors"
              >
                <UserMinus size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
